import type { EmailsThemeDto } from "@babackapp/private-api-client/types";
import { DEFAULT_FONT } from "./constants";
import { GOOGLE_FONT_FAMILIES } from "./google-fonts";

const GENERIC_FAMILIES = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
]);

// Google Fonts names are case-sensitive: "roboto condensed" is refused.
const GOOGLE_FONTS_BY_LOWER_NAME = new Map(
  GOOGLE_FONT_FAMILIES.map((family) => [family.toLowerCase(), family]),
);

type EmailFontsContent = {
  beforeBlocks?: unknown[] | null;
  insideBlocks?: unknown[] | null;
  afterBlocks?: unknown[] | null;
};

type FontBlock = {
  type?: string;
  font?: string | null;
  content?: { children?: unknown[] | null } | null;
};

/**
 * Keeps only what a CSS font stack needs. The backend already validates the
 * theme font; this guards the `<style>` it is written into.
 */
export function sanitizeFontFamily(font?: string | null): string | null {
  const sanitized = font?.replace(/[^\p{L}\p{N} ,'"_.-]/gu, "").trim();
  return sanitized || null;
}

/**
 * Families of a font stack, unquoted. Empty when the value is not a font
 * stack, e.g. CSS pasted in a block's font field.
 */
export function fontStackFamilies(stack: string): string[] {
  if (/[;:{}<>]/.test(stack)) return [];
  return stack
    .split(",")
    .map((family) =>
      family
        .trim()
        .replace(/^['"]|['"]$/g, "")
        .trim(),
    )
    .filter(Boolean);
}

/** Google Fonts family of that name, in its exact case; null when Google has none. */
export function googleFontFamily(name: string): string | null {
  return GOOGLE_FONTS_BY_LOWER_NAME.get(name.trim().toLowerCase()) ?? null;
}

/** Google Fonts name of the stack's first font, in its exact case; null otherwise. */
export function googleFontOf(stack?: string | null): string | null {
  const first = stack ? fontStackFamilies(stack)[0] : undefined;
  return first ? googleFontFamily(first) : null;
}

/**
 * Stylesheet of the given Google fonts, regular and bold. Google serves the
 * weights a font has and skips the missing ones.
 */
export function googleFontsStylesheetUrl(families: string[]): string | null {
  if (!families.length) return null;
  const params = families
    .map((family) => `family=${family.replace(/ /g, "+")}:wght@400;700`)
    .join("&");
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}

/** Fonts set on the blocks themselves, rows included. */
export function blockFontStacks(content?: EmailFontsContent | null): string[] {
  const stacks: string[] = [];
  const visit = (block: unknown) => {
    const b = block as FontBlock | null;
    if (!b) return;
    if (b.type === "row") (b.content?.children ?? []).forEach(visit);
    else if (b.font?.trim() && b.font !== DEFAULT_FONT) stacks.push(b.font);
  };
  [content?.beforeBlocks, content?.insideBlocks, content?.afterBlocks].forEach(
    (blocks) => (blocks ?? []).forEach(visit),
  );
  return stacks;
}

/**
 * Stylesheets an email needs: the theme's, then one for the Google fonts set
 * on its blocks. A system font such as Helvetica is never requested: Google
 * would answer with a lookalike.
 */
export function emailFontUrls(
  content: EmailFontsContent | null | undefined,
  theme: EmailsThemeDto | null | undefined,
): string[] {
  const urls = theme?.fontUrl ? [theme.fontUrl] : [];
  const themeFamily = googleFontOf(theme?.fontFamily);
  const blockFamilies = [
    ...new Set(blockFontStacks(content).map((stack) => googleFontOf(stack))),
  ].filter((family): family is string => !!family && family !== themeFamily);

  const blocksUrl = googleFontsStylesheetUrl(blockFamilies);
  return blocksUrl ? [...urls, blocksUrl] : urls;
}

/**
 * Font of a block: its own font when the merchant set one, else the theme
 * font. Blocks saved with the default stack written out count as unset. A
 * block font without a generic family gets the theme font as fallback, so a
 * client that cannot load it does not fall back to Times.
 */
export function resolveBlockFont(
  blockFont: string | null | undefined,
  theme: EmailsThemeDto | null | undefined,
): string {
  const themeFont = sanitizeFontFamily(theme?.fontFamily) ?? DEFAULT_FONT;
  if (!blockFont || blockFont === DEFAULT_FONT) return themeFont;

  const families = fontStackFamilies(blockFont);
  const hasGeneric = families.some((family) =>
    GENERIC_FAMILIES.has(family.toLowerCase()),
  );
  // Not a font stack (CSS pasted in the field): left as it renders today.
  if (!families.length || hasGeneric) return blockFont;
  return `${blockFont}, ${themeFont}`;
}

export { DEFAULT_FONT };
